import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';

import { Ga4AnalyticsService } from '../../../core/analytics/ga4-analytics.service';
import { AuthService } from '../services/auth.service';

@Component({
  selector: 'app-sign-in-page',
  imports: [ReactiveFormsModule],
  templateUrl: './sign-in-page.component.html',
})
export class SignInPageComponent {
  private readonly formBuilder = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly title = inject(Title);
  private readonly ga4AnalyticsService = inject(Ga4AnalyticsService);

  protected readonly isSubmitting = signal(false);
  protected readonly errorMessage = signal<string | null>(null);

  protected readonly signInForm = this.formBuilder.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required, Validators.minLength(6)]],
  });

  public constructor() {
    this.title.setTitle('Sign In');
    this.ga4AnalyticsService.trackPageView();
  }

  protected async onEmailSignIn(): Promise<void> {
    if (this.signInForm.invalid) {
      this.signInForm.markAllAsTouched();
      return;
    }

    const { email, password } = this.signInForm.getRawValue();
    await this.performSignIn(() => this.authService.signInWithEmailPassword(email, password));
  }

  protected async onGoogleSignIn(): Promise<void> {
    await this.performSignIn(() => this.authService.signInWithGoogle());
  }

  private async performSignIn(signInAction: () => Promise<void>): Promise<void> {
    this.isSubmitting.set(true);
    this.errorMessage.set(null);

    try {
      await signInAction();
      await this.router.navigateByUrl('/mynumerons');
    } catch (error) {
      this.errorMessage.set(this.resolveErrorMessage(error));
    } finally {
      this.isSubmitting.set(false);
    }
  }

  private resolveErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 401 || error.status === 403) {
        return 'Sign-in succeeded, but account access is not authorized by backend.';
      }

      if (error.status >= 500) {
        return 'Sign-in succeeded, but backend is unavailable right now. Please try again shortly.';
      }
    }

    const errorCode = this.extractErrorCode(error);
    if (!errorCode) {
      return 'Unable to sign in right now. Please verify credentials and try again.';
    }

    const firebaseMessages: Record<string, string> = {
      'auth/invalid-email': 'Please enter a valid email address.',
      'auth/invalid-credential': 'Invalid email or password.',
      'auth/user-disabled': 'This account has been disabled.',
      'auth/user-not-found': 'No account found for this email.',
      'auth/wrong-password': 'Incorrect password.',
      'auth/popup-closed-by-user': 'Google sign-in was cancelled.',
      'auth/popup-blocked': 'Google sign-in popup was blocked by browser settings.',
      'auth/network-request-failed':
        'Network error while signing in. Please check your connection.',
    };

    return (
      firebaseMessages[errorCode] ??
      'Unable to sign in right now. Please verify credentials and try again.'
    );
  }

  private extractErrorCode(error: unknown): string | null {
    if (!error || typeof error !== 'object') {
      return null;
    }

    const withCode = error as { code?: unknown };
    return typeof withCode.code === 'string' ? withCode.code : null;
  }
}
